import type { Block, FieldDefinition, SelectFieldConfig, SelectFieldOption, UsedOptions } from './block';
import type { LocalizedLabel, StageType } from './opportunity';

/**
 * `crm_configurations` — docs/CRM_SECTION_CONTRACT.md §2.
 *
 * **A configuration is a pipeline, and a team has one or more (AQ-11065).** `GET /configurations` lists
 * every active one (provisioning `Aquassist-1` if the team has none); every other read or write names
 * one by its `:configurationId`. `name` is a plain string, required, trimmed, at most 200 characters,
 * and unique per team ignoring case. A pipeline is created empty (`POST /configurations`, body
 * `{ name }`), renamed (`PATCH`, body `{ name }`), and deleted only while empty (`DELETE`) — the team's
 * last pipeline cannot be deleted.
 *
 * Landed before Tracks D, E and F start so none of them has to guess (§6, "Configuration CRM, split for
 * three developers"): D types the stage columns from here, E the block assignment, F the stage detail
 * and the field flags.
 */

/**
 * Sparse: only a field an admin has touched gets a row. Without one, a field is visible from the block's
 * starting stage and never required.
 */
export interface PlacedField {
    field: string;
    visibleFrom: string | null;
    requiredFrom: string | null;
    /**
     * AQ-10858, `select` only: the option **keys** (see `optionKey`) this team has taken **out** of the
     * field — the mockup's ✕ on a value chip. Pipeline-wide, so a value picked at one stage
     * does not vanish at the next.
     *
     * Stored as what was *removed*, not what was kept, so a value the super admin adds later is offered
     * to every team by default. An absent array means every option is kept.
     */
    removedOptions?: string[];
}

/**
 * A block placed once per pipeline, present from its starting stage to the last. `blocksAt()` derives a
 * stage's blocks.
 */
export interface PlacedBlock {
    /** → `crm_blocks._id`. The catalogue is global and live, so this may resolve to nothing (§4 rule 7). */
    block: string;
    /** One order for the whole pipeline, grouped by starting stage: earlier stages' blocks come first. */
    order: number;
    fromStage: string;
    fields: PlacedField[];
}

export interface Stage {
    id: string;
    key: string;
    order: number;
    label: LocalizedLabel;
    objective: LocalizedLabel;
    /** Column dot. Assigned server-side at creation, like a block's. */
    color: string;
    type: StageType;
}

export interface Configuration {
    id: string;
    name: string;
    stages: Stage[];
    blocks: PlacedBlock[];
}

/** `POST /configurations` and `PATCH /configurations/:configurationId` — the only writable field. */
export interface ConfigurationNamePayload {
    name: string;
}

/** `GET /configurations` — the pipelines the caller may see (AQ-11066), oldest first. */
export interface GetConfigurationsRes {
    configurations: Configuration[];
    defaultConfiguration: string | null;
}

/**
 * `PATCH /configurations/:configurationId/stages/:stageId` — **Track F**. Identity only: block
 * membership and field flags each have their own endpoint, so no two tracks share a handler.
 *
 * **Partial, and without `type`.** There is no save button: each paired FR/EN editor PATCHes the pair it
 * owns on blur, so a body carrying only `label` (or only `objective`) is the normal case rather than an
 * edge one. `type` is absent because Configuration CRM has no control for it and must not send one —
 * stages are purely configured and the outcome is set on the opportunity (§4 rule 10).
 */
export interface StageWritePayload {
    label?: LocalizedLabel;
    objective?: LocalizedLabel;
}

export interface PlaceBlockPayload {
    block: string;
    stage: string;
}

/** `order` is the position within the starting stage's blocks, so a block never goes above an earlier one. */
export interface UpdatePlacedBlockPayload {
    stage?: string;
    order?: number;
}

/** One row of `PATCH …/stages/:stageId/fields`: what the field should be **at that stage**. */
export interface StageFieldWrite {
    field: string;
    visible: boolean;
    required: boolean;
    removedOptions?: string[];
}

/** `PATCH /configurations/:configurationId/stages/:stageId/fields` — **Track F**. */
export interface StageFieldFlagsPayload {
    fields: StageFieldWrite[];
}

/**
 * What a removed value is stored as in `PlacedField.removedOptions`.
 *
 * Advanced config writes options as a bare `{ label }` — `SelectFieldOption.id` is not assigned yet — so
 * keying on the id alone would leave the ✕ inert on every real block. The fallback is the label pair,
 * whose failure mode is recoverable: renaming a value in Advanced config forgets the removal and the
 * value comes back, and the stale key drops out of the array on the row's next write. An index would
 * instead silently remove a *different* value after Advanced config's ↑ ↓ reorder, which is why it is
 * not the fallback. Drop the fallback once ids are assigned and backfilled.
 */
export const optionKey = (option: SelectFieldOption): string =>
    option.id ?? `label:${option.label.fr}|${option.label.en}`;

/** One of a `select` field's values, as Étape 2 and the wizard see it. */
export interface ResolvedStageFieldOption {
    option: SelectFieldOption;
    /** What a removal is stored as — `optionKey(option)`. */
    key: string;
    /** Kept by the team. A removed value is not offered by the wizard and shows no chip in Étape 2. */
    selected: boolean;
    /** Chosen on an opportunity, so it cannot be removed. */
    used: boolean;
}

export interface ResolvedStageField {
    definition: FieldDefinition;
    visible: boolean;
    required: boolean;
    visibleLocked: boolean;
    requiredLocked: boolean;
    /**
     * The field's values with the team's selection applied, in the catalogue's order. Empty for the ten
     * non-`select` types. The wizard renders the `selected` ones; Étape 2 renders them as chips and
     * offers the rest back.
     */
    options: ResolvedStageFieldOption[];
}

const stageOrderOf = (configuration: Configuration): Map<string, number> =>
    new Map(configuration.stages.map((stage) => [stage.id, stage.order]));

/** Whether `reference` is `stage` or an earlier one. A reference to a deleted stage never is. */
function startsBy(reference: string | null, stage: Stage, stageOrder: Map<string, number>): boolean {
    const order = reference === null ? undefined : stageOrder.get(reference);
    return order !== undefined && order <= stage.order;
}

export function blocksAt(configuration: Configuration, stage: Stage): PlacedBlock[] {
    const stageOrder = stageOrderOf(configuration);
    return configuration.blocks
        .filter((placed) => startsBy(placed.fromStage, stage, stageOrder))
        .sort((first, second) => first.order - second.order);
}

/**
 * Étape 2's rows for one placed block **at one stage**: the block's **current** fields, left-joined onto
 * the stored rows, in the catalogue's order.
 *
 * This is what keeps Tracks E and F out of each other's data — E places blocks and never seeds a field
 * row; F sets where fields start and never places a block. It also absorbs both live-catalogue cases for
 * free: a field added after the block was placed appears with the defaults, and a stored
 * row whose field has since been deleted is dropped instead of rendering as a ghost row.
 */
export function resolveStageFields(
    block: Block,
    placed: PlacedBlock,
    stage: Stage,
    configuration: Configuration,
    usedOptions: UsedOptions = {},
): ResolvedStageField[] {
    const stored = new Map(placed.fields.map((row) => [row.field, row]));
    const stageOrder = stageOrderOf(configuration);
    const startsBefore = (reference: string | null): boolean =>
        startsBy(reference, stage, stageOrder) && reference !== stage.id;

    return block.fields.map((definition) => {
        const row = stored.get(definition.id);
        const visibleFrom = row ? row.visibleFrom : placed.fromStage;
        const requiredFrom = row?.requiredFrom ?? null;
        const visible = startsBy(visibleFrom, stage, stageOrder);
        const required = visible && startsBy(requiredFrom, stage, stageOrder);
        return {
            definition,
            visible,
            required,
            visibleLocked: visible && startsBefore(visibleFrom),
            requiredLocked: required && startsBefore(requiredFrom),
            options: resolveFieldOptions(definition, row?.removedOptions, usedOptions[definition.id]),
        };
    });
}

/** A `select` field's values with the team's removals applied, keyed by `optionKey`. */
function resolveFieldOptions(
    definition: FieldDefinition,
    removedOptions: string[] | undefined,
    usedKeys: string[] = [],
): ResolvedStageFieldOption[] {
    if (definition.type !== 'select') {
        return [];
    }

    const removed = new Set(removedOptions ?? []);
    const used = new Set(usedKeys);

    return ((definition.config as SelectFieldConfig | undefined)?.options ?? []).map((option) => {
        const key = optionKey(option);
        return { option, key, selected: !removed.has(key), used: used.has(key) };
    });
}
